import { useCallback, useEffect, useState } from 'react'
import useApiService from 'services/api'
import { ALL_COUNTRIES } from 'utils/countries'
import { filterStoreRepo } from 'components/Filters/FilterStore'
import FilterModal, {
  FilterGroup,
  QuickFilterGroup,
} from 'components/Filters/components/FilterModal'
import { QuickShowHiddenFilter } from 'components/Filters/fields/QuickFilter'
import { SelectFilter } from 'components/Filters/fields/SelectFilter'
import { FilterIdentifier } from 'models/saved_view'
import { createFilterColumnHelper } from '../commonColumnHelpers'
import { UNMATCHED_DOORS_COLUMNS } from 'components/DataExpansionComponents/UnmatchedDoors/UnmatchedDoorsColumnsInfo'
import { CommonFiltersetDoorsAccountGroup } from './CommonFiltersetDoorsAccountGroup'

interface DoorsFiltersetProps {
  filterIdentifier: FilterIdentifier
  hideCampaignsFilter?: boolean
  subCategoryId?: number
}

export function DoorsFilterset(props: DoorsFiltersetProps) {
  const { filterIdentifier, hideCampaignsFilter, subCategoryId } = props

  const api = useApiService()
  // Get the city options - for unmatched doors we can use the company cities endpoint
  const { data: cityOptions } = api.useGetDoorProxyCities({
    subCategoryId,
    unmatched: true,
  })

  const { data: stateOptions } = api.useGetDoorProxyStates({
    subCategoryId,
    unmatched: true,
  })

  const [usSpecificFiltersVisible, setUsSpecificFiltersVisible] =
    useState<boolean>(true)

  const doorsFilterStore = filterStoreRepo.getStore(filterIdentifier)
  const { pendingValueFilters, clearPendingValueFilter } = doorsFilterStore()

  const clearUsFilters = useCallback(() => {
    if (pendingValueFilters.state) {
      clearPendingValueFilter('state')
    }
  }, [pendingValueFilters, clearPendingValueFilter])

  useEffect(() => {
    const selectedCountries = pendingValueFilters?.country?.map((f) => f.value)
    const showUsSpecificFilters = !(
      selectedCountries?.length > 0 && !selectedCountries.includes('US')
    )

    if (showUsSpecificFilters !== usSpecificFiltersVisible) {
      setUsSpecificFiltersVisible(showUsSpecificFilters)
    }

    if (!showUsSpecificFilters) {
      clearUsFilters()
    }
  }, [pendingValueFilters, clearUsFilters, usSpecificFiltersVisible])

  // Always use UNMATCHED_DOORS_COLUMNS for filtering since it has all needed fields
  const filterHelper = createFilterColumnHelper({
    columns: UNMATCHED_DOORS_COLUMNS,
  })

  return (
    <FilterModal identifier={filterIdentifier} store={doorsFilterStore}>
      <QuickFilterGroup>
        <QuickShowHiddenFilter filterStore={doorsFilterStore} />
      </QuickFilterGroup>
      <CommonFiltersetDoorsAccountGroup
        filterStore={doorsFilterStore}
        hideCampaignsFilter={hideCampaignsFilter}
      />
      <FilterGroup title="Location">
        <SelectFilter
          {...filterHelper.retrieveProps('country')}
          options={ALL_COUNTRIES}
          filterStore={doorsFilterStore}
        />
        <SelectFilter
          {...filterHelper.retrieveProps('city')}
          options={cityOptions as any}
          filterStore={doorsFilterStore}
        />
        {usSpecificFiltersVisible && (
          <SelectFilter
            {...filterHelper.retrieveProps('state')}
            options={stateOptions}
            filterStore={doorsFilterStore}
          />
        )}
      </FilterGroup>
    </FilterModal>
  )
}
